import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import { Link, useLocation, useParams } from 'react-router-dom';
import SEOHead from '../../components/SEO/SEOHead';
import { blogPosts } from '../../data/blogData';
import { db } from '../../services/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import ProgressiveLaunchpadWizard from './ProgressiveLaunchpadWizard';
import { useTheme } from '../../contexts/ThemeContext';

// Icons
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import LanguageIcon from '@mui/icons-material/Language';
import DevicesIcon from '@mui/icons-material/Devices';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import SpeedIcon from '@mui/icons-material/Speed';
import ArticleIcon from '@mui/icons-material/Article';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import DiscountIcon from '@mui/icons-material/Discount';
import {
  LAUNCHPAD_PACKAGES,
  calculatePricing,
  DEFAULT_COUPON_CODE,
  VALID_COUPONS,
} from '../../data/packagesData';

const LandingPage = ({ isStandaloneLaunchpad = false, openWizardImmediately = false }) => {
  const { themeMode } = useTheme();
  const isLight = themeMode === 'light';

  // Dynamic Theme Palette Tokens
  const bgCanvas = isLight ? '#f8fafc' : '#0b0c0e';
  const bgSurface = isLight ? '#ffffff' : '#131418';
  const bgSurfaceAlt = isLight ? '#f1f5f9' : '#0e1015';
  const bgCardSubtle = isLight ? '#ffffff' : 'rgba(255, 255, 255, 0.02)';
  const bgInput = isLight ? '#ffffff' : 'rgba(255, 255, 255, 0.03)';
  const textPrimary = isLight ? '#0f172a' : '#f7f7f8';
  const textSecondary = isLight ? '#475569' : '#94a3b8';
  const textBody = isLight ? '#334155' : '#cbd5e1';
  const textMuted = isLight ? '#64748b' : '#64748b';
  const borderCard = isLight ? '#e2e8f0' : 'rgba(247, 247, 248, 0.08)';
  const borderSubtle = isLight ? '#e2e8f0' : 'rgba(247, 247, 248, 0.06)';
  const borderInput = isLight ? '#cbd5e1' : 'rgba(247, 247, 248, 0.12)';
  const cardShadow = isLight ? '0 4px 20px -2px rgba(0, 0, 0, 0.06), 0 2px 6px -1px rgba(0, 0, 0, 0.04)' : 'none';
  const heroGradient = isLight
    ? 'radial-gradient(circle at 50% 10%, rgba(6, 182, 212, 0.15) 0%, rgba(248, 250, 252, 0) 70%)'
    : 'radial-gradient(circle at 50% 10%, rgba(6, 182, 212, 0.12) 0%, rgba(11, 12, 14, 0) 70%)';
  const headlineGradient = isLight
    ? 'linear-gradient(135deg, #0f172a 0%, #334155 50%, #0284c7 100%)'
    : 'linear-gradient(135deg, #ffffff 0%, #cbd5e1 50%, #38bdf8 100%)';
  const location = useLocation();
  const routeParams = useParams() || {};
  const routeLeadId = routeParams.leadId;
  const isLaunchpadRoute = isStandaloneLaunchpad || (Boolean(location?.pathname) && location.pathname.includes('launchpad'));

  // Progressive Micro-Commitment Wizard State
  const [isWizardOpen, setIsWizardOpen] = useState(Boolean(openWizardImmediately || routeLeadId));
  const [wizardLeadId, setWizardLeadId] = useState(routeLeadId || null);
  const [wizardStep, setWizardStep] = useState(routeLeadId ? 3 : 1);
  const [wizardMode, setWizardMode] = useState('callback'); // 'callback' | 'blueprint'

  // Onboarding Form State (Default: Starter Launchpad - ₹4,999 Inaugural Package)
  const [formData, setFormData] = useState({
    companyName: '',
    founderName: '',
    phone: '',
    email: '',
    preferredDomain: '',
    packageType: 'Starter Launchpad',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Discount & Coupon State (Default: 50% Launch Discount via LAUNCH50)
  const [couponCode, setCouponCode] = useState(DEFAULT_COUPON_CODE);
  const [appliedCoupon, setAppliedCoupon] = useState(DEFAULT_COUPON_CODE);
  const [couponStatus, setCouponStatus] = useState({
    valid: true,
    msg: '🎉 50% Launch Discount Applied (LAUNCH50)',
  });

  const handleApplyCoupon = (overrideCode) => {
    const targetCode = (overrideCode !== undefined ? overrideCode : couponCode).trim().toUpperCase();
    if (!targetCode) {
      setAppliedCoupon('');
      setCouponStatus({ valid: false, msg: 'No discount coupon applied.' });
      return;
    }
    if (VALID_COUPONS[targetCode] || targetCode === DEFAULT_COUPON_CODE) {
      setAppliedCoupon(targetCode);
      const coupon = VALID_COUPONS[targetCode];
      setCouponStatus({
        valid: true,
        msg: `✓ ${coupon ? coupon.label : '50% Launch Discount'} Applied (${targetCode})`,
      });
    } else {
      setCouponStatus({
        valid: false,
        msg: 'Invalid code. Use LAUNCH50 for 50% off.',
      });
    }
  };

  React.useEffect(() => {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const codeParam = searchParams.get('code') || searchParams.get('coupon');
      const pkgParam = searchParams.get('pkg') || searchParams.get('package');
      const resumeParam = searchParams.get('resume') || routeLeadId;
      const wizardParam = searchParams.get('wizard');

      if (resumeParam) {
        setWizardLeadId(resumeParam);
        setWizardStep(3); // Resume right onto payment step
        setIsWizardOpen(true);
      } else if (wizardParam === 'true' || openWizardImmediately) {
        setIsWizardOpen(true);
      }

      if (window.location.hash === '#onboard' || window.location.hash === '#onboarding-wizard') {
        setIsWizardOpen(true);
      }

      if (codeParam) {
        handleApplyCoupon(codeParam);
      }
      if (pkgParam) {
        const lower = pkgParam.toLowerCase();
        if (lower.includes('mvp')) {
          setFormData(prev => ({ ...prev, packageType: '2–4 Week MVP Development' }));
        } else if (lower.includes('growth')) {
          setFormData(prev => ({ ...prev, packageType: 'Growth Enterprise Launchpad' }));
        } else {
          setFormData(prev => ({ ...prev, packageType: 'Starter Launchpad' }));
        }
      }
    } catch {
      // ignore
    }

    const handleOpenOnboardingEvent = (e) => {
      if (e?.detail?.pkg) {
        setFormData(prev => ({ ...prev, packageType: e.detail.pkg }));
      }
      setIsWizardOpen(true);
    };

    window.addEventListener('verveai:open-onboarding', handleOpenOnboardingEvent);
    return () => {
      window.removeEventListener('verveai:open-onboarding', handleOpenOnboardingEvent);
    };
  }, [routeLeadId, openWizardImmediately]);

  const starterPricing = calculatePricing(9999, appliedCoupon);
  const growthPricing = calculatePricing(19999, appliedCoupon);

  const selectedPkg = LAUNCHPAD_PACKAGES.find(p => p.name === formData.packageType) || LAUNCHPAD_PACKAGES[0];
  const currentPricing = calculatePricing(selectedPkg?.originalPrice, appliedCoupon);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.companyName || !formData.founderName || !formData.phone || !formData.email) {
      setErrorMsg('Please fill in your company name, founder name, phone, and email.');
      return;
    }

    setSubmitting(true);
    setErrorMsg('');

    try {
      await addDoc(collection(db, 'inquiries'), {
        name: formData.founderName,
        founderName: formData.founderName,
        email: formData.email,
        phone: formData.phone,
        company: formData.companyName,
        startupName: formData.companyName,
        preferredDomain: formData.preferredDomain || null,
        packageType: formData.packageType || 'Turnkey Startup Launchpad',
        message: formData.notes || `Startup Launchpad inquiry for ${formData.companyName}`,
        originalPrice: currentPricing.originalPrice,
        discountPercent: currentPricing.discountPercent,
        discountAmount: currentPricing.discountAmount,
        finalPrice: currentPricing.finalPrice,
        couponCode: currentPricing.couponCode || null,
        currency: 'INR',
        status: 'new',
        priority: 'high',
        source: 'https://verveai.co',
        submittedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Error saving onboarding request:', err);
      // Fallback success for graceful user experience even if offline
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const pageTitle = isLaunchpadRoute
    ? 'Startup Launchpad — Turnkey Digital Foundations in 48–72 Hrs | VerveAI'
    : 'VerveAI — Turnkey Startup & Digital Launchpad | Powered by Verve AI Labs';

  const pageDescription = isLaunchpadRoute
    ? 'Turnkey digital launchpad for newly incorporated businesses & startups: official brand domains (.in/.com), high-speed web storefront, custom domain business email with SPF/DKIM deliverability, and 24/7 AI WhatsApp customer receptionists. Starting at ₹4,999.'
    : 'We set up everything newly incorporated companies need to operate online: official brand domains (.in/.com), lightning-fast web storefront, custom domain business email with SPF/DKIM deliverability, and 24/7 AI WhatsApp customer receptionists.';

  const canonicalUrl = isLaunchpadRoute
    ? 'https://verveai.co/launchpad'
    : 'https://verveai.co/';

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": "https://verveai.co/#website",
        "url": "https://verveai.co",
        "name": "VerveAI",
        "publisher": { "@id": "https://verveai.co/#organization" },
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://verveai.co/blog?q={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      },
      {
        "@type": "Organization",
        "@id": "https://verveai.co/#organization",
        "name": "VerveAI",
        "alternateName": "VerveAI Startup Launchpad",
        "url": "https://verveai.co",
        "parentOrganization": {
          "@type": "Organization",
          "name": "Verve AI Labs",
          "url": "https://verveailabs.com",
          "description": "Enterprise AI Engineering, Custom MCP Servers, and Autonomous Agent Systems"
        },
        "description": "VerveAI: Turnkey Day-1 digital foundations and rapid 2–4 week custom AI MVP development.",
        "sameAs": [
          "https://verveailabs.com"
        ]
      },
      {
        "@type": "Service",
        "@id": isLaunchpadRoute ? "https://verveai.co/launchpad#service" : "https://verveai.co/#service",
        "name": "VerveAI Startup Launchpad & Custom AI MVP Development",
        "provider": { "@id": "https://verveai.co/#organization" },
        "serviceType": "Digital Infrastructure Setup & AI Software Engineering",
        "areaServed": "Global",
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Startup Infrastructure & AI MVP Packages",
          "itemListElement": [
            {
              "@type": "Offer",
              "name": "Starter Launchpad (48–72 Hrs)",
              "description": "1 Brand Domain (.in/.com), High-speed 5-page web storefront, Custom domain corporate email, SPF/DKIM DNS deliverability setup, and 24/7 AI WhatsApp customer receptionist.",
              "price": "4999",
              "priceCurrency": "INR",
              "priceValidUntil": "2027-12-31",
              "availability": "https://schema.org/InStock",
              "url": "https://verveai.co/launchpad"
            },
            {
              "@type": "Offer",
              "name": "2–4 Week MVP Development (Custom AI & Full-Stack)",
              "description": "Production full-stack web/mobile apps, custom AI agents, self-healing workflow automation, vector RAG pipelines, and 100% IP ownership.",
              "availability": "https://schema.org/InStock",
              "url": "https://verveai.co/?pkg=mvp"
            },
            {
              "@type": "Offer",
              "name": "Growth Enterprise Launchpad",
              "description": "Complete multi-pillar digital footprint for newly registered Pvt Ltds & LLPs: 3 brand domains, up to 5 custom domain corporate mailboxes, full SPF/DKIM/DMARC suite, and 24/7 AI customer receptionist.",
              "price": "9999",
              "priceCurrency": "INR",
              "priceValidUntil": "2027-12-31",
              "availability": "https://schema.org/InStock",
              "url": "https://verveai.co/launchpad"
            }
          ]
        }
      },
      {
        "@type": "FAQPage",
        "@id": "https://verveai.co/#faq",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "What is included in the ₹4,999 Startup Launchpad?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "The ₹4,999 Starter Launchpad includes 1 official brand domain (.in or .com), a high-speed 5-page web storefront, 1 custom domain corporate email account, cryptographic SPF/DKIM/DMARC DNS security configuration, and a 24/7 AI WhatsApp customer receptionist delivered in 48–72 hours."
            }
          },
          {
            "@type": "Question",
            "name": "How does the LAUNCH50 coupon code work?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "The LAUNCH50 coupon code provides an inaugural 50% discount on VerveAI turnkey launchpad packages, reducing the Starter Launchpad from ₹9,999 to ₹4,999 one-time."
            }
          },
          {
            "@type": "Question",
            "name": "How is VerveAI related to Verve AI Labs?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "VerveAI (verveai.co) is the dedicated Startup and SMB arm of Verve AI Labs (verveailabs.com). While Verve AI Labs engineers enterprise-grade agentic swarms, custom Model Context Protocol (MCP) servers, and private cloud AI architectures, VerveAI brings high-velocity Day-1 launchpads and 2–4 week custom AI MVPs to early-stage founders."
            }
          },
          {
            "@type": "Question",
            "name": "How does VerveAI replace Zapier or Make.com workflows?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "VerveAI replaces brittle no-code automations with custom-coded, containerized AI microservices featuring idempotent webhook queues, deterministic error handling, and self-healing workers—cutting recurring task run costs by up to 80% while retaining 100% IP ownership."
            }
          },
          {
            "@type": "Question",
            "name": "What is the delivery timeline for a custom AI MVP?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "VerveAI builds and deploys production-ready custom AI MVPs in 2 to 4 weeks using structured agile sprints, delivering full-stack Next.js/React applications, custom AI agent logic, auth, database, and payments."
            }
          }
        ]
      },
      ...(isLaunchpadRoute ? [
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://verveai.co/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Startup Launchpad",
              "item": "https://verveai.co/launchpad"
            }
          ]
        }
      ] : [])
    ]
  };

  const featuredPosts = blogPosts.slice(0, 3);

  return (
    <Box sx={{ bgcolor: bgCanvas, color: textPrimary, minHeight: '100vh', overflowX: 'hidden' }}>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        canonical={canonicalUrl}
        jsonLd={schema}
      />

      {/* ============================================================== */}
      {/* HERO SECTION */}
      {/* ============================================================== */}
      <Box
        id="onboard"
        sx={{
          position: 'relative',
          pt: { xs: 8, md: 14 },
          pb: { xs: 10, md: 14 },
          background: 'radial-gradient(circle at 50% 10%, rgba(6, 182, 212, 0.12) 0%, rgba(11, 12, 14, 0) 70%)',
          borderBottom: `1px solid ${borderSubtle}`,
          scrollMarginTop: '100px',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', maxWidth: '880px', mx: 'auto' }}>
            {/* Reverse Bridge Pill Badge */}
            <Box
              component="a"
              href="https://verveailabs.com"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.2,
                bgcolor: 'rgba(6, 182, 212, 0.08)',
                border: '1px solid rgba(6, 182, 212, 0.3)',
                px: 2.2,
                py: 0.8,
                borderRadius: '999px',
                mb: 3.5,
                textDecoration: 'none',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: 'rgba(6, 182, 212, 0.16)',
                  borderColor: 'rgba(6, 182, 212, 0.6)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 20px rgba(6, 182, 212, 0.25)',
                },
              }}
            >
              <RocketLaunchIcon sx={{ fontSize: 16, color: '#22d3ee' }} />
              <Typography
                variant="caption"
                sx={{
                  color: isLight ? '#334155' : '#e2e8f0',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  letterSpacing: '0.01em',
                }}
              >
                Backed by{' '}
                <Box
                  component="span"
                  sx={{
                    color: '#22d3ee',
                    fontWeight: 700,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  Verve AI Labs ↗
                </Box>
              </Typography>
            </Box>

            {/* Main Headline */}
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.4rem', sm: '3.4rem', md: '4.2rem' },
                fontWeight: 800,
                lineHeight: 1.15,
                letterSpacing: '-0.03em',
                mb: 3,
                background: headlineGradient,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Turnkey Digital Onboarding.
              <br />
              <Box component="span" sx={{ color: '#06b6d4', WebkitTextFillColor: '#06b6d4' }}>
                Custom AI for Every Business Need.
              </Box>
            </Typography>

            {/* Subtitle */}
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1rem', md: '1.2rem' },
                color: textSecondary,
                lineHeight: 1.7,
                mb: 5,
                maxWidth: '740px',
                mx: 'auto',
              }}
            >
              We help businesses establish their complete verified digital presence in 48–72 hours, and engineer production AI solutions—from 24/7 intelligent customer receptionists to bespoke automated workflows and custom MVPs.
            </Typography>

            {/* Hero CTAs - Streamlined to 2 Clear Actions */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mb: 6 }}>
              <Button
                onClick={() => {
                  setFormData(prev => ({ ...prev, packageType: 'Starter Launchpad' }));
                  handleApplyCoupon(DEFAULT_COUPON_CODE);
                  setIsWizardOpen(true);
                }}
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #06b6d4 0%, #0284c7 100%)',
                  color: '#ffffff',
                  px: 4,
                  py: 1.6,
                  fontSize: '1rem',
                  fontWeight: 700,
                  borderRadius: '10px',
                  boxShadow: '0 8px 24px rgba(6, 182, 212, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #22d3ee 0%, #0369a1 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 30px rgba(6, 182, 212, 0.4)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Get Onboarded (48-Hr Launch)
              </Button>

              <Button
                component="a"
                href="#ai-solutions"
                variant="outlined"
                size="large"
                sx={{
                  borderColor: isLight ? '#cbd5e1' : 'rgba(247, 247, 248, 0.15)',
                  color: textPrimary,
                  bgcolor: isLight ? '#ffffff' : 'rgba(255, 255, 255, 0.03)',
                  px: 3.5,
                  py: 1.6,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: '10px',
                  '&:hover': {
                    borderColor: 'rgba(6, 182, 212, 0.4)',
                    bgcolor: 'rgba(6, 182, 212, 0.06)',
                  },
                }}
              >
                Explore AI Solutions ↓
              </Button>
            </Box>

            {/* Trust Badges Bar */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: { xs: 2, md: 4 },
                pt: 3,
                borderTop: `1px solid ${borderSubtle}`,
              }}
            >
              {[
                '100% In-House Provisioning',
                '48-Hour Turnkey Deployment',
                'SPF/DKIM Spam Protection',
                'Pre-Trained AI Receptionist',
              ].map((badge) => (
                <Box key={badge} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon sx={{ fontSize: 16, color: '#10b981' }} />
                  <Typography variant="caption" sx={{ color: textBody, fontSize: '0.82rem', fontWeight: 500 }}>
                    {badge}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ============================================================== */}
      {/* 1. DIGITAL ONBOARDING PILLARS */}
      {/* ============================================================== */}
      <Box id="onboarding" sx={{ py: { xs: 10, md: 14 }, borderBottom: `1px solid ${borderSubtle}`, scrollMarginTop: '80px' }}>
        <Box id="solutions" sx={{ position: 'relative', top: '-80px', visibility: 'hidden' }} />
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8, maxWidth: '760px', mx: 'auto' }}>
            <Typography
              variant="caption"
              sx={{
                color: '#06b6d4',
                fontSize: '0.8rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                display: 'block',
                mb: 1.5,
              }}
            >
              COMPLETE DIGITAL ONBOARDING
            </Typography>
            <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.8rem' }, fontWeight: 800, letterSpacing: '-0.02em', mb: 2 }}>
              Foundational Digital Presence in 48–72 Hours
            </Typography>
            <Typography variant="body1" sx={{ color: textSecondary, fontSize: '1rem', lineHeight: 1.6 }}>
              Do not lose weeks juggling separate domain registrars, web developers, email DNS errors, and missed phone calls. We set up all 4 foundational pillars under one turnkey roof.
            </Typography>
          </Box>

          <Grid container spacing={3.5}>
            {/* Pillar 1 */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  bgcolor: bgSurface,
                  border: `1px solid ${borderCard}`,
                  borderRadius: '16px',
                  height: '100%',
                  p: 1,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'rgba(6, 182, 212, 0.4)',
                    boxShadow: '0 12px 30px rgba(6, 182, 212, 0.1)',
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      bgcolor: 'rgba(6, 182, 212, 0.1)',
                      border: '1px solid rgba(6, 182, 212, 0.25)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2.5,
                    }}
                  >
                    <LanguageIcon sx={{ color: '#06b6d4', fontSize: 26 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', mb: 1.5, color: textPrimary }}>
                    1. Brand Domains & DNS Security
                  </Typography>
                  <Typography variant="body2" sx={{ color: textSecondary, fontSize: '0.88rem', lineHeight: 1.6 }}>
                    Lock down your official name across <strong>.in</strong>, <strong>.co.in</strong>, and <strong>.com</strong> before domain squatters claim them. Includes Cloudflare SSL encryption and nameserver defense.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Pillar 2 */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  bgcolor: bgSurface,
                  border: `1px solid ${borderCard}`,
                  borderRadius: '16px',
                  height: '100%',
                  p: 1,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'rgba(56, 189, 248, 0.4)',
                    boxShadow: '0 12px 30px rgba(56, 189, 248, 0.1)',
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      bgcolor: 'rgba(56, 189, 248, 0.1)',
                      border: '1px solid rgba(56, 189, 248, 0.25)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2.5,
                    }}
                  >
                    <DevicesIcon sx={{ color: '#38bdf8', fontSize: 26 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', mb: 1.5, color: textPrimary }}>
                    2. High-Speed Web Storefront
                  </Typography>
                  <Typography variant="body2" sx={{ color: textSecondary, fontSize: '0.88rem', lineHeight: 1.6 }}>
                    A blazing-fast, mobile-optimized modern website engineered for trust. Features sub-second load times, service showcases, contact triggers, and structured schema so Google indexes your brand fast.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Pillar 3 */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  bgcolor: bgSurface,
                  border: `1px solid ${borderCard}`,
                  borderRadius: '16px',
                  height: '100%',
                  p: 1,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'rgba(16, 185, 129, 0.4)',
                    boxShadow: '0 12px 30px rgba(16, 185, 129, 0.1)',
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      bgcolor: 'rgba(16, 185, 129, 0.1)',
                      border: '1px solid rgba(16, 185, 129, 0.25)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2.5,
                    }}
                  >
                    <MarkEmailReadIcon sx={{ color: '#10b981', fontSize: 26 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', mb: 1.5, color: textPrimary }}>
                    3. Corporate Email & Deliverability
                  </Typography>
                  <Typography variant="body2" sx={{ color: textSecondary, fontSize: '0.88rem', lineHeight: 1.6 }}>
                    Official addresses (<code>founder@brand.in</code>) on your company custom domain. We configure cryptographic SPF, DKIM, and DMARC DNS records so your client proposals never land in spam folders.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Pillar 4 */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  bgcolor: bgSurface,
                  border: `1px solid ${borderCard}`,
                  borderRadius: '16px',
                  height: '100%',
                  p: 1,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'rgba(168, 85, 247, 0.4)',
                    boxShadow: '0 12px 30px rgba(168, 85, 247, 0.1)',
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      bgcolor: 'rgba(168, 85, 247, 0.1)',
                      border: '1px solid rgba(168, 85, 247, 0.25)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2.5,
                    }}
                  >
                    <ShieldOutlinedIcon sx={{ color: '#a855f7', fontSize: 26 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', mb: 1.5, color: textPrimary }}>
                    4. Verified Business Presence
                  </Typography>
                  <Typography variant="body2" sx={{ color: textSecondary, fontSize: '0.88rem', lineHeight: 1.6 }}>
                    Google Business Profile setup and local map optimization. Establish local geographic authority, enable customer reviews, and display official corporate legitimacy to search engines.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ============================================================== */}
      {/* 2. APPLIED AI SOLUTIONS — SOLVING ANY BUSINESS AI NEED */}
      {/* ============================================================== */}
      <Box id="ai-solutions" sx={{ py: { xs: 10, md: 16 }, borderBottom: `1px solid ${borderSubtle}`, scrollMarginTop: '80px' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8, maxWidth: '780px', mx: 'auto' }}>
            <Typography
              variant="caption"
              sx={{
                color: '#a855f7',
                fontSize: '0.8rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                display: 'block',
                mb: 1.5,
              }}
            >
              SOLVING ANY BUSINESS AI NEED
            </Typography>
            <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.8rem' }, fontWeight: 800, letterSpacing: '-0.02em', mb: 2 }}>
              Applied AI Engineering for Modern Businesses
            </Typography>
            <Typography variant="body1" sx={{ color: textSecondary, fontSize: '1rem', lineHeight: 1.6 }}>
              Whether you need an intelligent receptionist that handles inbound customers 24/7, deterministic workflow automation that replaces fragile no-code tools, or a full-stack custom AI MVP—we engineer production AI that drives actual revenue.
            </Typography>
          </Box>

          <Grid container spacing={3.5} alignItems="stretch">
            {/* AI Solution 1: 24/7 Verina AI WhatsApp Customer Receptionist */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: { xs: 3, sm: 4 },
                  borderRadius: '20px',
                  bgcolor: bgSurface,
                  border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(6, 182, 212, 0.3)',
                  boxShadow: cardShadow,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#06b6d4',
                    transform: 'translateY(-4px)',
                    boxShadow: isLight ? '0 12px 30px rgba(6, 182, 212, 0.12)' : '0 12px 30px rgba(6, 182, 212, 0.15)',
                  },
                }}
              >
                <Box sx={{ width: 52, height: 52, borderRadius: '14px', bgcolor: 'rgba(6, 182, 212, 0.1)', border: '1px solid rgba(6, 182, 212, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5 }}>
                  <SmartToyIcon sx={{ color: '#06b6d4', fontSize: 28 }} />
                </Box>

                <Chip
                  label="INCLUDED IN LAUNCHPAD"
                  size="small"
                  sx={{
                    alignSelf: 'flex-start',
                    bgcolor: 'rgba(6, 182, 212, 0.12)',
                    color: isLight ? '#0891b2' : '#22d3ee',
                    border: '1px solid rgba(6, 182, 212, 0.3)',
                    fontWeight: 800,
                    fontSize: '10px',
                    mb: 1.5,
                  }}
                />

                <Typography variant="h5" sx={{ fontWeight: 800, fontSize: '1.25rem', mb: 1.5, color: textPrimary }}>
                  24/7 AI Customer Receptionist
                </Typography>
                <Typography variant="body2" sx={{ color: textSecondary, fontSize: '0.9rem', lineHeight: 1.6, mb: 3 }}>
                  Pre-trained on your specific product catalog and company FAQs. Answers customer inquiries instantly on WhatsApp, qualifies buying intent, and immediately notifies you when a high-value client needs attention.
                </Typography>

                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mb: 3, flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                  {[
                    'Zero missed leads: Responds in under 2 seconds',
                    'Multi-turn conversational intent qualification',
                    'Direct lead routing to your personal WhatsApp',
                    'Powered by Gemini LLM with zero hallucination guardrails',
                  ].map((item) => (
                    <Box component="li" key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, color: textBody, fontSize: '0.84rem' }}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: '#06b6d4', mt: 0.2, shrink: 0 }} />
                      <span>{item}</span>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>

            {/* AI Solution 2: Intelligent Workflow & Process Automation */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: { xs: 3, sm: 4 },
                  borderRadius: '20px',
                  bgcolor: bgSurface,
                  border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(16, 185, 129, 0.3)',
                  boxShadow: cardShadow,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#10b981',
                    transform: 'translateY(-4px)',
                    boxShadow: isLight ? '0 12px 30px rgba(16, 185, 129, 0.12)' : '0 12px 30px rgba(16, 185, 129, 0.15)',
                  },
                }}
              >
                <Box sx={{ width: 52, height: 52, borderRadius: '14px', bgcolor: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5 }}>
                  <SpeedIcon sx={{ color: '#10b981', fontSize: 28 }} />
                </Box>

                <Chip
                  label="REPLACES ZAPIER & MAKE"
                  size="small"
                  sx={{
                    alignSelf: 'flex-start',
                    bgcolor: 'rgba(16, 185, 129, 0.12)',
                    color: isLight ? '#059669' : '#34d399',
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                    fontWeight: 800,
                    fontSize: '10px',
                    mb: 1.5,
                  }}
                />

                <Typography variant="h5" sx={{ fontWeight: 800, fontSize: '1.25rem', mb: 1.5, color: textPrimary }}>
                  Intelligent Workflow Automation
                </Typography>
                <Typography variant="body2" sx={{ color: textSecondary, fontSize: '0.9rem', lineHeight: 1.6, mb: 3 }}>
                  Stop duct-taping no-code tools that break on schema changes. We build custom-coded, containerized AI automation pipelines with deterministic error handling and automatic retry queues.
                </Typography>

                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mb: 3, flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                  {[
                    'Automated document extraction & invoice processing',
                    'Deterministic webhook pipelines with self-healing workers',
                    'Up to 80% cheaper recurring operational costs vs Zapier',
                    '100% private execution: No company data shared with 3rd parties',
                  ].map((item) => (
                    <Box component="li" key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, color: textBody, fontSize: '0.84rem' }}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: '#10b981', mt: 0.2, shrink: 0 }} />
                      <span>{item}</span>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>

            {/* AI Solution 3: Custom 2–4 Week AI MVP & Software Engineering */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: { xs: 3, sm: 4 },
                  borderRadius: '20px',
                  bgcolor: bgSurface,
                  border: '2px solid #a855f7',
                  boxShadow: isLight ? '0 12px 36px rgba(168, 85, 247, 0.16)' : '0 0 35px rgba(168, 85, 247, 0.2)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#c084fc',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 16px 40px rgba(168, 85, 247, 0.25)',
                  },
                }}
              >
                <Box sx={{ width: 52, height: 52, borderRadius: '14px', bgcolor: 'rgba(168, 85, 247, 0.1)', border: '1px solid rgba(168, 85, 247, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5 }}>
                  <RocketLaunchIcon sx={{ color: '#a855f7', fontSize: 28 }} />
                </Box>

                <Chip
                  label="2–4 WEEK AGILE SPRINTS"
                  size="small"
                  sx={{
                    alignSelf: 'flex-start',
                    bgcolor: 'rgba(168, 85, 247, 0.15)',
                    color: isLight ? '#9333ea' : '#c084fc',
                    border: '1px solid rgba(168, 85, 247, 0.35)',
                    fontWeight: 800,
                    fontSize: '10px',
                    mb: 1.5,
                  }}
                />

                <Typography variant="h5" sx={{ fontWeight: 800, fontSize: '1.25rem', mb: 1.5, color: textPrimary }}>
                  Custom AI MVPs & Apps
                </Typography>
                <Typography variant="body2" sx={{ color: textSecondary, fontSize: '0.9rem', lineHeight: 1.6, mb: 3 }}>
                  Turn your AI idea into a commercial reality in 14–28 days. Full-stack Next.js/React web or mobile applications, vector RAG search over company knowledge, custom auth, and payment integrations.
                </Typography>

                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mb: 3, flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                  {[
                    'Production full-stack application with lead architect',
                    'Vector embeddings & retrieval-augmented generation (RAG)',
                    'Secure database, user authentication & payment gateways',
                    '100% full IP handover: Zero royalties, your code forever',
                  ].map((item) => (
                    <Box component="li" key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, color: textBody, fontSize: '0.84rem' }}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: '#a855f7', mt: 0.2, shrink: 0 }} />
                      <span>{item}</span>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ============================================================== */}
      {/* HOW IT WORKS */}
      {/* ============================================================== */}
      <Box id="how-it-works" sx={{ py: { xs: 10, md: 14 }, borderBottom: `1px solid ${borderSubtle}`, scrollMarginTop: '80px' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8, maxWidth: '720px', mx: 'auto' }}>
            <Typography variant="caption" sx={{ color: '#06b6d4', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', mb: 1.5 }}>
              STREAMLINED 4-STEP LAUNCHPAD BLUEPRINT
            </Typography>
            <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.6rem' }, fontWeight: 800, letterSpacing: '-0.02em', mb: 2 }}>
              From Raw Concept to Fully Live in 48–72 Hours
            </Typography>
            <Typography variant="body1" sx={{ color: '#94a3b8', fontSize: '1rem', lineHeight: 1.6 }}>
              Zero bureaucracy, zero technical complexity. Our progressive launch sequence handles your domain, high-speed storefront, custom domain business email deliverability, and autonomous AI Receptionist.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {[
              {
                step: '01',
                time: '10 SECONDS',
                title: 'Check Domain & Reserve Coupon',
                desc: 'Enter your brand name, domain preference (.in / .com), and WhatsApp. We verify live domain readiness and lock in your 50% inaugural discount (LAUNCH50) instantly.',
              },
              {
                step: '02',
                time: '3 CLICKS',
                title: 'Scope Your Blueprint',
                desc: 'Select your target industry vibe (D2C, SaaS, Services, Healthcare) and configure your AI Receptionist conversion goals with zero tedious paperwork.',
              },
              {
                step: '03',
                time: '48–72 HOURS',
                title: 'In-House Engineering Sprint',
                desc: 'Our engineers provision Cloudflare SSL & DNS, build your 5-page responsive web storefront, and configure custom domain corporate inboxes with SPF/DKIM deliverability.',
              },
              {
                step: '04',
                time: 'DAY-1 READY',
                title: 'Deploy Verina AI & Go Live',
                desc: 'Activate your autonomous 24/7 Verina AI Receptionist powered by Gemini LLM. Ready Day-1 to answer inquiries, qualify leads, and close deals with 100% full admin ownership.',
              },
            ].map((item) => (
              <Grid item xs={12} sm={6} lg={3} key={item.step}>
                <Box
                  onClick={() => setIsWizardOpen(true)}
                  sx={{
                    p: 3.5,
                    borderRadius: '16px',
                    bgcolor: 'rgba(255, 255, 255, 0.02)',
                    border: `1px solid ${borderCard}`,
                    height: '100%',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'all 0.25s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      bgcolor: 'rgba(6, 182, 212, 0.04)',
                      borderColor: 'rgba(6, 182, 212, 0.35)',
                      transform: 'translateY(-3px)',
                      boxShadow: '0 12px 28px rgba(0, 0, 0, 0.35)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '2.2rem',
                        fontWeight: 800,
                        color: 'rgba(6, 182, 212, 0.45)',
                        fontFamily: 'monospace',
                        lineHeight: 1,
                      }}
                    >
                      {item.step}
                    </Typography>
                    <Box
                      sx={{
                        fontSize: '9px',
                        fontWeight: 800,
                        letterSpacing: '0.06em',
                        color: '#22d3ee',
                        bgcolor: 'rgba(6, 182, 212, 0.1)',
                        border: '1px solid rgba(6, 182, 212, 0.25)',
                        px: 1,
                        py: 0.3,
                        borderRadius: '999px',
                      }}
                    >
                      {item.time}
                    </Box>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem', mb: 1.5, color: textPrimary }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.6, flexGrow: 1 }}>
                    {item.desc}
                  </Typography>
                  <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${borderSubtle}`, display: 'flex', alignItems: 'center', gap: 0.5, color: '#06b6d4', fontSize: '0.78rem', fontWeight: 600 }}>
                    <span>Launch Step</span> →
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Interactive Launch CTA */}
          <Box sx={{ mt: 7, textAlign: 'center' }}>
            <Button
              onClick={() => setIsWizardOpen(true)}
              variant="contained"
              size="large"
              sx={{
                background: 'linear-gradient(135deg, #06b6d4 0%, #0284c7 100%)',
                color: '#ffffff',
                fontWeight: 700,
                fontSize: '0.95rem',
                px: 4,
                py: 1.5,
                borderRadius: '10px',
                boxShadow: '0 8px 24px rgba(6, 182, 212, 0.25)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, #22d3ee 0%, #0369a1 100%)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 12px 30px rgba(6, 182, 212, 0.4)',
                },
              }}
            >
              Start Your Onboarding (Takes 30s) →
            </Button>
            <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#64748b', fontSize: '0.78rem' }}>
              Inaugural 50% discount automatically applied (Code: LAUNCH50) • Zero commitment to start
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ============================================================== */}
      {/* PACKAGES / PRICING — CONCISE SINGLE-VIEW COMPARISON */}
      {/* ============================================================== */}
      <Box id="packages" sx={{ py: { xs: 7, md: 9 }, borderBottom: `1px solid ${borderSubtle}`, scrollMarginTop: '80px' }}>
        <Container maxWidth="lg">
          {/* Header Block with Integrated Inaugural Benefit */}
          <Box sx={{ textAlign: 'center', mb: { xs: 3.5, md: 4.5 }, maxWidth: '780px', mx: 'auto' }}>
            <Chip
              icon={<DiscountIcon sx={{ fontSize: '14px !important', color: '#0b0c0e !important' }} />}
              label="CLEAR TRANSPARENT PRICING • 50% OFF CODE: LAUNCH50 APPLIED"
              size="small"
              sx={{
                bgcolor: '#06b6d4',
                color: '#0b0c0e',
                fontWeight: 800,
                fontSize: '11px',
                letterSpacing: '0.04em',
                px: 1,
                py: 1.5,
                borderRadius: '8px',
                mb: 1.5,
              }}
            />
            <Typography variant="h2" sx={{ fontSize: { xs: '1.9rem', md: '2.5rem' }, fontWeight: 800, letterSpacing: '-0.02em', mb: 1, color: textPrimary }}>
              Choose Your Launchpad Velocity
            </Typography>
            <Typography variant="body1" sx={{ color: textSecondary, fontSize: { xs: '0.88rem', md: '0.95rem' }, lineHeight: 1.6 }}>
              Compare all 3 trajectories side-by-side in a single view. Transparent pricing, zero vendor lock-in, and guaranteed in-house delivery.
            </Typography>
          </Box>

          {/* Unified 3-Column Fast Comparison Grid */}
          <Grid container spacing={2.5} alignItems="stretch" justifyContent="center">
            {/* TIER 1: Starter Launchpad */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: { xs: 2.5, sm: 3 },
                  borderRadius: '18px',
                  bgcolor: bgSurface,
                  border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(6, 182, 212, 0.35)',
                  boxShadow: cardShadow,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    borderColor: '#06b6d4',
                    transform: 'translateY(-3px)',
                    boxShadow: isLight ? '0 12px 30px rgba(6, 182, 212, 0.12)' : '0 12px 30px rgba(6, 182, 212, 0.15)',
                  },
                }}
              >
                {/* Header Row */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: textPrimary, fontSize: '1.15rem' }}>
                    Starter Launchpad
                  </Typography>
                  <Chip
                    label="48–72 HRS"
                    size="small"
                    sx={{
                      bgcolor: isLight ? '#ecfeff' : 'rgba(6, 182, 212, 0.15)',
                      color: isLight ? '#0891b2' : '#22d3ee',
                      border: '1px solid rgba(6, 182, 212, 0.3)',
                      fontWeight: 700,
                      fontSize: '10px',
                      height: '22px',
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: textSecondary, fontSize: '0.82rem', mb: 2, lineHeight: 1.4, minHeight: '34px' }}>
                  Complete Day-1 digital setup for newly incorporated businesses & solo founders.
                </Typography>

                {/* Price Display */}
                <Box sx={{ mb: 2, p: 1.8, borderRadius: '12px', bgcolor: isLight ? '#f8fafc' : 'rgba(6, 182, 212, 0.05)', border: `1px solid ${borderSubtle}` }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 900, color: '#06b6d4', fontSize: '1.9rem', lineHeight: 1 }}>
                      {starterPricing.formattedFinalPrice}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', textDecoration: 'line-through', fontWeight: 600, fontSize: '0.95rem' }}>
                      {starterPricing.formattedOriginalPrice}
                    </Typography>
                    <Chip
                      size="small"
                      label="50% OFF"
                      sx={{ bgcolor: 'rgba(16, 185, 129, 0.15)', color: '#10b981', fontWeight: 800, fontSize: '9px', height: '20px', border: '1px solid rgba(16, 185, 129, 0.3)' }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 600, display: 'block', mt: 0.4, fontSize: '11px' }}>
                    🎉 Save ₹5,000 via inaugural code LAUNCH50
                  </Typography>
                </Box>

                {/* Direct Action CTA */}
                <Button
                  onClick={() => {
                    setFormData(prev => ({ ...prev, packageType: 'Starter Launchpad' }));
                    handleApplyCoupon(DEFAULT_COUPON_CODE);
                    setIsWizardOpen(true);
                  }}
                  variant="contained"
                  fullWidth
                  sx={{
                    background: 'linear-gradient(135deg, #06b6d4 0%, #0284c7 100%)',
                    color: '#ffffff',
                    py: 1.1,
                    fontWeight: 700,
                    fontSize: '0.88rem',
                    borderRadius: '9px',
                    boxShadow: '0 4px 14px rgba(6, 182, 212, 0.3)',
                    mb: 2.5,
                    '&:hover': { background: 'linear-gradient(135deg, #22d3ee 0%, #0369a1 100%)' },
                  }}
                >
                  Select Starter ({starterPricing.formattedFinalPrice}) →
                </Button>

                {/* Deliverables Checklist */}
                <Box sx={{ pt: 1.5, borderTop: `1px solid ${borderSubtle}`, flexGrow: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '10px', display: 'block', mb: 1.2 }}>
                    Included In Starter:
                  </Typography>
                  <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {[
                      '1 Official Brand Domain (.in / .com) + SSL',
                      '5-Page Fast Web Storefront (Sub-second)',
                      '1 Custom Domain Corporate Inbox',
                      'SPF, DKIM & DMARC Deliverability Suite',
                      '24/7 AI WhatsApp Customer Receptionist',
                      'Guaranteed Turnaround: 48–72 Hours',
                    ].map((feat) => (
                      <Box component="li" key={feat} sx={{ display: 'flex', alignItems: 'center', gap: 1, color: textBody, fontSize: '0.82rem' }}>
                        <CheckCircleIcon sx={{ fontSize: 15, color: '#06b6d4', shrink: 0 }} />
                        <span>{feat}</span>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* TIER 2: 2–4 Week Custom MVP (Featured & Elevated) */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: { xs: 2.5, sm: 3 },
                  borderRadius: '18px',
                  bgcolor: bgSurface,
                  border: '2px solid #a855f7',
                  boxShadow: isLight ? '0 12px 36px rgba(168, 85, 247, 0.16)' : '0 0 35px rgba(168, 85, 247, 0.2)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  transform: { md: 'scale(1.02)' },
                  zIndex: 2,
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    borderColor: '#c084fc',
                    boxShadow: '0 16px 40px rgba(168, 85, 247, 0.25)',
                  },
                }}
              >
                {/* Header Row with Featured Badge */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: textPrimary, fontSize: '1.15rem' }}>
                    2–4 Week Custom MVP
                  </Typography>
                  <Chip
                    label="MOST POPULAR • 2–4 WKS"
                    size="small"
                    sx={{
                      background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
                      color: '#ffffff',
                      fontWeight: 800,
                      fontSize: '9px',
                      height: '22px',
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: textSecondary, fontSize: '0.82rem', mb: 2, lineHeight: 1.4, minHeight: '34px' }}>
                  Full-stack production app & AI agent workflows replacing brittle Zapier spaghetti.
                </Typography>

                {/* Price Display */}
                <Box sx={{ mb: 2, p: 1.8, borderRadius: '12px', bgcolor: isLight ? '#faf5ff' : 'rgba(168, 85, 247, 0.06)', border: isLight ? '1px solid #e9d5ff' : '1px solid rgba(168, 85, 247, 0.25)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 900, color: '#a855f7', fontSize: '1.9rem', lineHeight: 1 }}>
                      Custom Scope
                    </Typography>
                    <Box component="span" sx={{ fontSize: '0.8rem', color: textSecondary, fontWeight: 500 }}>
                      / sprint
                    </Box>
                  </Box>
                  <Typography variant="caption" sx={{ color: '#a855f7', fontWeight: 600, display: 'block', mt: 0.4, fontSize: '11px' }}>
                    ⚡ 100% In-House Client IP • Fixed Milestones
                  </Typography>
                </Box>

                {/* Direct Action CTA */}
                <Button
                  onClick={() => {
                    setFormData(prev => ({ ...prev, packageType: '2–4 Week MVP Development' }));
                    setIsWizardOpen(true);
                  }}
                  variant="contained"
                  fullWidth
                  sx={{
                    background: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)',
                    color: '#ffffff',
                    py: 1.1,
                    fontWeight: 700,
                    fontSize: '0.88rem',
                    borderRadius: '9px',
                    boxShadow: '0 4px 16px rgba(168, 85, 247, 0.35)',
                    mb: 2.5,
                    '&:hover': { background: 'linear-gradient(135deg, #c084fc 0%, #6d28d9 100%)' },
                  }}
                >
                  Scope Your Custom MVP (2–4 Wks) →
                </Button>

                {/* Deliverables Checklist */}
                <Box sx={{ pt: 1.5, borderTop: `1px solid ${borderSubtle}`, flexGrow: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#a855f7', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '10px', display: 'block', mb: 1.2 }}>
                    Included In Custom MVP:
                  </Typography>
                  <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {[
                      'Full-Stack Web or Mobile App (Next.js/React)',
                      'Custom Multi-Step AI Agent Workflows',
                      'Production Vector RAG & LLM Reasoning',
                      'Database, Auth & Payment Gateways',
                      '100% Client Code Ownership & IP Handover',
                      '14–28 Days Agile Delivery with Lead Architect',
                    ].map((feat) => (
                      <Box component="li" key={feat} sx={{ display: 'flex', alignItems: 'center', gap: 1, color: textBody, fontSize: '0.82rem' }}>
                        <CheckCircleIcon sx={{ fontSize: 15, color: '#a855f7', shrink: 0 }} />
                        <span>{feat}</span>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* TIER 3: Growth Enterprise Launchpad */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: { xs: 2.5, sm: 3 },
                  borderRadius: '18px',
                  bgcolor: bgSurface,
                  border: isLight ? '1px solid #cbd5e1' : `1px solid ${borderCard}`,
                  boxShadow: cardShadow,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    borderColor: '#10b981',
                    transform: 'translateY(-3px)',
                    boxShadow: isLight ? '0 12px 30px rgba(16, 185, 129, 0.12)' : '0 12px 30px rgba(16, 185, 129, 0.15)',
                  },
                }}
              >
                {/* Header Row */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: textPrimary, fontSize: '1.15rem' }}>
                    Growth Enterprise
                  </Typography>
                  <Chip
                    label="48 HRS EXPRESS"
                    size="small"
                    sx={{
                      bgcolor: isLight ? '#ecfdf5' : 'rgba(16, 185, 129, 0.15)',
                      color: isLight ? '#059669' : '#34d399',
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                      fontWeight: 700,
                      fontSize: '10px',
                      height: '22px',
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: textSecondary, fontSize: '0.82rem', mb: 2, lineHeight: 1.4, minHeight: '34px' }}>
                  Multi-domain lockdown & up to 5 Google corporate inboxes for expanding LLPs & Pvt Ltds.
                </Typography>

                {/* Price Display */}
                <Box sx={{ mb: 2, p: 1.8, borderRadius: '12px', bgcolor: isLight ? '#f8fafc' : 'rgba(16, 185, 129, 0.05)', border: `1px solid ${borderSubtle}` }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 900, color: isLight ? '#059669' : '#34d399', fontSize: '1.9rem', lineHeight: 1 }}>
                      {growthPricing.formattedFinalPrice}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', textDecoration: 'line-through', fontWeight: 600, fontSize: '0.95rem' }}>
                      {growthPricing.formattedOriginalPrice}
                    </Typography>
                    <Chip
                      size="small"
                      label="50% OFF"
                      sx={{ bgcolor: 'rgba(16, 185, 129, 0.15)', color: '#10b981', fontWeight: 800, fontSize: '9px', height: '20px', border: '1px solid rgba(16, 185, 129, 0.3)' }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 600, display: 'block', mt: 0.4, fontSize: '11px' }}>
                    🎉 Save ₹10,000 via inaugural code LAUNCH50
                  </Typography>
                </Box>

                {/* Direct Action CTA */}
                <Button
                  onClick={() => {
                    setFormData(prev => ({ ...prev, packageType: 'Growth Enterprise Launchpad' }));
                    handleApplyCoupon(DEFAULT_COUPON_CODE);
                    setIsWizardOpen(true);
                  }}
                  variant="outlined"
                  fullWidth
                  sx={{
                    borderColor: isLight ? '#10b981' : 'rgba(16, 185, 129, 0.4)',
                    color: isLight ? '#059669' : '#34d399',
                    bgcolor: isLight ? '#ecfdf5' : 'rgba(16, 185, 129, 0.05)',
                    py: 1.1,
                    fontWeight: 700,
                    fontSize: '0.88rem',
                    borderRadius: '9px',
                    mb: 2.5,
                    '&:hover': {
                      borderColor: '#10b981',
                      bgcolor: isLight ? '#d1fae5' : 'rgba(16, 185, 129, 0.15)',
                    },
                  }}
                >
                  Select Growth ({growthPricing.formattedFinalPrice}) →
                </Button>

                {/* Deliverables Checklist */}
                <Box sx={{ pt: 1.5, borderTop: `1px solid ${borderSubtle}`, flexGrow: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '10px', display: 'block', mb: 1.2 }}>
                    Included In Growth:
                  </Typography>
                  <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {[
                      '3 Brand Domains (.in + .com + .co.in)',
                      'High-Converting Multi-Page Web Storefront',
                      'Custom Domain Business Email (Up to 5 team inboxes)',
                      'Full SPF, DKIM & DMARC Deliverability Suite',
                      '24/7 AI WhatsApp Multi-Agent Routing',
                      'Google Business Profile & Map Listing (48 Hrs)',
                    ].map((feat) => (
                      <Box component="li" key={feat} sx={{ display: 'flex', alignItems: 'center', gap: 1, color: textBody, fontSize: '0.82rem' }}>
                        <CheckCircleIcon sx={{ fontSize: 15, color: '#10b981', shrink: 0 }} />
                        <span>{feat}</span>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Single-View Comparative Trust Footer */}
          <Box
            sx={{
              mt: 3.5,
              p: 2,
              borderRadius: '14px',
              bgcolor: isLight ? '#f1f5f9' : 'rgba(255, 255, 255, 0.02)',
              border: `1px solid ${borderSubtle}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#10b981', shrink: 0 }} />
              <Typography variant="body2" sx={{ color: textSecondary, fontSize: '0.84rem' }}>
                All packages include <strong>100% In-House Intellectual Property</strong>, zero recurring platform fees, and white-glove DNS configuration.
              </Typography>
            </Box>
            <Button
              component="a"
              href={`https://wa.me/919415967302?text=${encodeURIComponent('Hi VerveAI! I want to discuss the best package for my business.')}`}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<WhatsAppIcon />}
              size="small"
              sx={{
                color: isLight ? '#059669' : '#34d399',
                fontWeight: 700,
                fontSize: '0.82rem',
                textTransform: 'none',
                '&:hover': { bgcolor: isLight ? '#ecfdf5' : 'rgba(16, 185, 129, 0.1)' },
              }}
            >
              Need custom guidance? Chat on WhatsApp (+91 9415967302) ↗
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ============================================================== */}
      <Box sx={{ py: { xs: 10, md: 14 }, bgcolor: bgCanvas, borderTop: `1px solid ${borderSubtle}` }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              position: 'relative',
              p: { xs: 4, sm: 6, md: 8 },
              borderRadius: '24px',
              bgcolor: bgSurface,
              border: '1px solid rgba(6, 182, 212, 0.35)',
              boxShadow: isLight ? '0 10px 40px rgba(0, 0, 0, 0.08)' : '0 20px 60px rgba(0, 0, 0, 0.7), 0 0 50px rgba(6, 182, 212, 0.12)',
              overflow: 'hidden',
            }}
          >
            {/* Luminous Glow Backdrop */}
            <Box
              aria-hidden="true"
              sx={{
                pointerEvents: 'none',
                position: 'absolute',
                top: '-100px',
                right: '15%',
                width: '450px',
                height: '350px',
                borderRadius: '50%',
                background: 'radial-gradient(ellipse at center, rgba(6, 182, 212, 0.18) 0%, transparent 70%)',
                filter: 'blur(70px)',
              }}
            />

            <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, alignItems: { xs: 'flex-start', lg: 'center' }, justifyContent: 'space-between', gap: 6 }}>
              <Box sx={{ maxWidth: '780px' }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    bgcolor: 'rgba(6, 182, 212, 0.1)',
                    border: '1px solid rgba(6, 182, 212, 0.3)',
                    px: 2,
                    py: 0.5,
                    borderRadius: '999px',
                    mb: 2.5,
                  }}
                >
                  <ShieldOutlinedIcon sx={{ fontSize: 15, color: '#22d3ee' }} />
                  <Typography variant="caption" sx={{ color: '#22d3ee', fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    ENTERPRISE RESEARCH & ENGINEERING STUDIO • VERVE AI LABS
                  </Typography>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { xs: '1.8rem', sm: '2.4rem', md: '2.8rem' },
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                    lineHeight: 1.2,
                    mb: 2.5,
                    color: textPrimary,
                  }}
                >
                  Need Enterprise-Grade Agentic Swarms, Custom MCP Servers, or Private Cloud AI?
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: textBody,
                    fontSize: { xs: '0.95rem', md: '1.05rem' },
                    lineHeight: 1.7,
                    mb: 4,
                  }}
                >
                  For large-scale architectural engineering and in-house SFT/RLHF model alignment, visit our parent research & engineering studio at{' '}
                  <Box
                    component="a"
                    href="https://verveailabs.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: '#22d3ee',
                      fontWeight: 700,
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      '&:hover': { textDecoration: 'underline', color: '#38bdf8' },
                    }}
                  >
                    Verve AI Labs ↗
                  </Box>.
                </Typography>

                {/* 4 Capability Highlights mirroring Labs */}
                <Grid container spacing={2}>
                  {[
                    { title: 'Custom MCP Servers', desc: 'Deterministic tool calling & private data connectors' },
                    { title: 'Agentic Reasoning Swarms', desc: 'Multi-agent orchestration with audit telemetry' },
                    { title: 'In-House SFT / RLHF', desc: 'Bespoke alignment on proprietary enterprise data' },
                    { title: 'Zero Vendor Lock-in', desc: 'Deploy to private AWS, GCP, Azure, or on-prem VPC' },
                  ].map((cap) => (
                    <Grid item xs={12} sm={6} key={cap.title}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.2, p: 1.5, borderRadius: '10px', bgcolor: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.05)' }}>
                        <CheckCircleIcon sx={{ fontSize: 16, color: '#06b6d4', mt: 0.3, shrink: 0 }} />
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: textPrimary, fontWeight: 700, fontSize: '0.85rem' }}>
                            {cap.title}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.75rem', lineHeight: 1.4, display: 'block' }}>
                            {cap.desc}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* Right Side Callout / Actions */}
              <Box sx={{ width: { xs: '100%', lg: '320px' }, shrink: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  component="a"
                  href="https://verveailabs.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<OpenInNewIcon />}
                  variant="contained"
                  size="large"
                  fullWidth
                  sx={{
                    background: 'linear-gradient(135deg, #06b6d4 0%, #0284c7 100%)',
                    color: '#ffffff',
                    py: 1.6,
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    borderRadius: '12px',
                    boxShadow: '0 8px 25px rgba(6, 182, 212, 0.35)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #22d3ee 0%, #0369a1 100%)',
                      boxShadow: '0 12px 30px rgba(6, 182, 212, 0.45)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  Visit Verve AI Labs Studio ↗
                </Button>

                <Button
                  component="a"
                  href="https://verveailabs.com/contact"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="large"
                  fullWidth
                  sx={{
                    borderColor: 'rgba(6, 182, 212, 0.3)',
                    color: textBody,
                    bgcolor: 'rgba(6, 182, 212, 0.05)',
                    py: 1.4,
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    borderRadius: '12px',
                    '&:hover': {
                      borderColor: '#06b6d4',
                      color: '#ffffff',
                      bgcolor: 'rgba(6, 182, 212, 0.12)',
                    },
                  }}
                >
                  Schedule Architecture Review
                </Button>
              </Box>
            </Box>
          </Box>
      </Container>
    </Box>

      {/* 4-Step Progressive Lock-In & Micro-Commitment Wizard */}
      <ProgressiveLaunchpadWizard
        isOpen={isWizardOpen}
        onClose={() => setIsWizardOpen(false)}
        initialLeadId={wizardLeadId}
        initialStep={wizardStep}
        initialMode={wizardMode}
      />
    </Box>
  );
};

export default LandingPage;
